import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { 
  AuthContextType, 
  AuthState, 
  LoginData, 
  RegisterData, 
  VerificationData,
  User as AuthUser
} from '../types/auth';
import { 
  getSecureItem,
  setSecureItem, 
  removeSecureStorage,
  clearSecureStorage 
} from '../utils/secureStorage';
import i18n from '../i18n';

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: false,
  error: null
};

type AuthAction =
  | { type: 'LOGIN_REQUEST' }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_REQUEST' }
  | { type: 'REGISTER_SUCCESS'; payload: AuthUser }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'VERIFICATION_REQUEST' }
  | { type: 'VERIFICATION_SUCCESS' }
  | { type: 'VERIFICATION_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: AuthUser }
  | { type: 'CLEAR_ERROR' };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_REQUEST':
    case 'REGISTER_REQUEST':
    case 'VERIFICATION_REQUEST':
      return {
        ...state,
        loading: true,
        error: null
      };
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null
      };
    case 'VERIFICATION_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null
      };
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
    case 'VERIFICATION_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    case 'LOGOUT':
      return {
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null
      };
    case 'SET_USER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
        loading: false
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const createLocalUser = (provider: 'google' | 'microsoft'): AuthUser => {
    const providerName = provider === 'google' ? 'Google' : 'Microsoft';
    const now = new Date().toISOString();

    return {
      id: Date.now(),
      email: `${provider}@portfolio.local`,
      username: `${provider}_user`,
      first_name: providerName,
      last_name: 'User',
      country: 'N/A',
      phone: 'N/A',
      is_active: true,
      is_verified: true,
      created_at: now,
      updated_at: now
    };
  };

  useEffect(() => {
    const token = getSecureItem<string>('token');
    const user = getSecureItem<AuthUser>('user');

    if (!token || !user) {
      removeSecureStorage('token');
      removeSecureStorage('user');
      dispatch({ type: 'LOGOUT' });
      return;
    }

    dispatch({ type: 'SET_USER', payload: user });
  }, []);

  const getErrorMessage = (error: any): string => {
    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }
    return i18n.t('auth.errors.generic');
  };

  const login = async (data: LoginData): Promise<boolean> => {
    void data;
    dispatch({ type: 'LOGIN_REQUEST' });

    dispatch({
      type: 'LOGIN_FAILURE',
      payload: i18n.t('auth.errors.emailPasswordRemoved')
    });
    return false;
  };

  const loginWithProvider = async (provider: 'google' | 'microsoft'): Promise<boolean> => {
    dispatch({ type: 'LOGIN_REQUEST' });

    try {
      const userData = createLocalUser(provider);
      const token = `local-${provider}-${Date.now().toString(36)}`;
      setSecureItem('token', token);
      setSecureItem('user', userData);
      dispatch({ type: 'SET_USER', payload: userData });
      return true;
    } catch (error: any) {
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: getErrorMessage(error)
      });
      return false;
    }
  };

  const loginWithGoogle = async (): Promise<boolean> => {
    return loginWithProvider('google');
  };

  const loginWithMicrosoft = async (): Promise<boolean> => {
    return loginWithProvider('microsoft');
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    void data;
    dispatch({ type: 'REGISTER_REQUEST' });
    dispatch({
      type: 'REGISTER_FAILURE',
      payload: i18n.t('auth.errors.registerDisabled')
    });
    return false;
  };

  const verify = async (data: VerificationData): Promise<boolean> => {
    void data;
    dispatch({ type: 'VERIFICATION_REQUEST' });
    dispatch({
      type: 'VERIFICATION_FAILURE',
      payload: i18n.t('auth.errors.verifyDisabled')
    });
    return false;
  };

  const logout = () => {
    clearSecureStorage();
    dispatch({ type: 'LOGOUT' });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    register,
    verify,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 